import React from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

/**
 * LoadingStates - Comprehensive loading state components
 * 
 * Components:
 * - Spinner: Basic loading spinner
 * - Skeleton: Skeleton loading screens
 * - Dots: Dots loading animation
 * - Progress: Progress bar
 * - Shimmer: Shimmer effect
 */

// Spinner Component
export const Spinner = ({ 
  size = 'medium', 
  color = 'primary', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    medium: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <div 
      className={cn(
        'spinner',
        sizeClasses[size],
        className
      )}
      style={{ color: `var(--color-${color})` }}
      aria-label="Loading"
    />
  );
};

// Dots Loading Component
export const DotsLoading = ({ 
  size = 'medium', 
  color = 'primary', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    medium: 'w-2 h-2',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4'
  };

  return (
    <div 
      className={cn(
        'dots-loading',
        className
      )}
      style={{ color: `var(--color-${color})` }}
      aria-label="Loading"
    >
      <span className={sizeClasses[size]} />
      <span className={sizeClasses[size]} />
      <span className={sizeClasses[size]} />
    </div>
  );
};

// Progress Bar Component
export const ProgressBar = ({ 
  value = 0, 
  max = 100, 
  size = 'medium', 
  color = 'primary',
  showPercentage = false,
  className = '' 
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeClasses = {
    sm: 'h-1',
    medium: 'h-2',
    lg: 'h-3',
    xl: 'h-4'
  };

  return (
    <div className={cn('w-full', className)}>
      <div className={cn(
        'w-full bg-muted rounded-full overflow-hidden',
        sizeClasses[size]
      )}>
        <div
          className={cn(
            'h-full transition-all duration-300 ease-out',
            `bg-${color}`
          )}
          style={{ 
            width: `${percentage}%`,
            backgroundColor: `var(--color-${color})`
          }}
        />
      </div>
      {showPercentage && (
        <div className="text-sm text-muted-foreground mt-1 text-center">
          {Math.round(percentage)}%
        </div>
      )}
    </div>
  );
};

// Skeleton Component
export const Skeleton = ({ 
  className = '', 
  variant = 'text',
  width,
  height 
}) => {
  const variantClasses = {
    text: 'h-4 w-full',
    title: 'h-6 w-3/4',
    avatar: 'h-10 w-10 rounded-full',
    button: 'h-10 w-24',
    card: 'h-32 w-full',
    image: 'h-48 w-full'
  };

  return (
    <div
      className={cn(
        'skeleton rounded',
        variantClasses[variant],
        className
      )}
      style={{ 
        width: width || undefined,
        height: height || undefined
      }}
      aria-label="Loading content"
    />
  );
};

// Shimmer Effect Component
export const Shimmer = ({ 
  className = '', 
  children,
  isLoading = false 
}) => {
  if (!isLoading) return children;

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div className="skeleton absolute inset-0" />
      <div className="relative opacity-0">
        {children}
      </div>
    </div>
  );
};

// Card Skeleton Component
export const CardSkeleton = ({ className = '' }) => (
  <div className={cn('p-6 space-y-4', className)}>
    <div className="flex items-center space-x-4">
      <Skeleton variant="avatar" />
      <div className="space-y-2 flex-1">
        <Skeleton variant="title" />
        <Skeleton variant="text" className="w-2/3" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton variant="text" />
      <Skeleton variant="text" />
      <Skeleton variant="text" className="w-1/2" />
    </div>
    <div className="flex space-x-2">
      <Skeleton variant="button" />
      <Skeleton variant="button" />
    </div>
  </div>
);

// Table Skeleton Component
export const TableSkeleton = ({ 
  rows = 5, 
  columns = 4, 
  className = '' 
}) => (
  <div className={cn('space-y-3', className)}>
    {/* Header */}
    <div className="flex space-x-4">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" className="flex-1" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-4">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} variant="text" className="flex-1" />
        ))}
      </div>
    ))}
  </div>
);

// List Skeleton Component
export const ListSkeleton = ({ 
  items = 5, 
  showAvatar = true,
  className = '' 
}) => (
  <div className={cn('space-y-4', className)}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center space-x-4">
        {showAvatar && <Skeleton variant="avatar" />}
        <div className="space-y-2 flex-1">
          <Skeleton variant="text" />
          <Skeleton variant="text" className="w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

// Page Skeleton Component
export const PageSkeleton = ({ className = '' }) => (
  <div className={cn('space-y-8', className)}>
    {/* Header */}
    <div className="space-y-4">
      <Skeleton variant="title" className="w-1/3" />
      <Skeleton variant="text" className="w-2/3" />
    </div>
    
    {/* Content Grid */}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
    
    {/* Footer */}
    <div className="flex justify-center space-x-4">
      <Skeleton variant="button" />
      <Skeleton variant="button" />
    </div>
  </div>
);

// Loading Overlay Component
export const LoadingOverlay = ({ 
  isLoading = false, 
  children,
  message = 'Loading...',
  className = '' 
}) => {
  if (!isLoading) return children;

  return (
    <div className={cn('relative', className)}>
      <div className="absolute inset-0 bg-background backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center space-y-4">
          <Spinner size="lg" />
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
      {children}
    </div>
  );
};

// Inline Loading Component
export const InlineLoading = ({ 
  message = 'Loading...',
  size = 'medium',
  className = '' 
}) => (
  <div className={cn('flex items-center space-x-2', className)}>
    <Spinner size={size} />
    <span className="text-sm text-muted-foreground">{message}</span>
  </div>
);

// Button Loading Component
export const ButtonLoading = ({ 
  loading = false, 
  children,
  loadingText = 'Loading...',
  className = ''
}) => (
  <div className={cn('flex items-center space-x-2', className)}>
    {loading && <Spinner size="sm" />}
    <span>{loading ? loadingText : children}</span>
  </div>
);

// Button Loader Component (alias for ButtonLoading)
export const ButtonLoader = ButtonLoading;

// Export all components
export default {
  Spinner,
  DotsLoading,
  ProgressBar,
  Skeleton,
  Shimmer,
  CardSkeleton,
  TableSkeleton,
  ListSkeleton,
  PageSkeleton,
  LoadingOverlay,
  InlineLoading,
  ButtonLoading,
  ButtonLoader
};